import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import { useGoBack } from "../../../hooks/useGoBack";
import {
  activateCampaignApi,
  adStatusLabel,
  budgetLabel,
  deleteAdApi,
  deleteCampaignApi,
  endCampaignApi,
  fmtDate,
  fmtDateTime,
  fmtInt,
  fmtMoney,
  applySuggestionApi,
  campaignToSuggestion,
  getCampaignApi,
  suggestionToPayload,
  getInsightsApi,
  pauseCampaignApi,
  publishCampaignApi,
  STATUS_LABEL,
  STATUS_PILL,
  syncCampaignApi,
  type CampaignAd,
  type CampaignDetail,
  type CampaignInsightDay,
} from "../campanhasApi";
import AdFormModal from "../components/AdFormModal";
import AdImage from "../components/AdImage";
import AdPreviewModal from "../components/AdPreviewModal";
import AiAnalysisPanel from "../components/AiAnalysisPanel";
import CampaignCreativePanel from "../components/CampaignCreativePanel";
import CreativePickerModal from "../components/CreativePickerModal";
import UseCreativeAsAdModal from "../components/UseCreativeAsAdModal";
import SuggestionRefineChat from "../components/SuggestionRefineChat";
import AiAssistantDrawer from "../components/AiAssistantDrawer";
import ApplicationsPanel from "../components/ApplicationsPanel";
import DailyChart from "../components/DailyChart";
import { CAMPANHAS_PAGE, CAMPANHA_EDIT_PAGE, CAMPANHA_PROXIMA_PAGE } from "../routes";
import "./Campanhas.css";
import "./Criativos.css";

type Tab = "resultados" | "anuncios" | "candidatos" | "configuracao";
type Action = "publish" | "activate" | "pause" | "end" | "delete";

const TABS: { value: Tab; label: string }[] = [
  { value: "resultados", label: "Resultados" },
  { value: "anuncios", label: "Anúncios" },
  { value: "candidatos", label: "Candidatos" },
  { value: "configuracao", label: "Configuração" },
];

const AD_TONE: Record<string, string> = { ok: "ecoPill", warn: "ecoPill cmpPill--warn", bad: "ecoPill cmpPill--bad", off: "ecoPill ecoPill--off" };

/** A campanha: ações na Meta, resultados, anúncios, candidatos e a configuração. */
const CampanhaPage: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const goBack = useGoBack(CAMPANHAS_PAGE());
  const [params, setParams] = useSearchParams();
  const tabParam = params.get("aba") as Tab | null;
  const [c, setC] = useState<CampaignDetail | null>(null);
  const [days, setDays] = useState<CampaignInsightDay[] | null>(null);
  const [confirm, setConfirm] = useState<Action | null>(null);
  const [adModal, setAdModal] = useState<{ ad: CampaignAd | null } | null>(null);
  const [adToDelete, setAdToDelete] = useState<CampaignAd | null>(null);
  const [adToPreview, setAdToPreview] = useState<CampaignAd | null>(null);
  const [busy, setBusy] = useState(false);
  // Escolher uma arte já gerada pela Eco-IA para virar anúncio.
  const [picking, setPicking] = useState(false);
  const [pickedCreative, setPickedCreative] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [detail, insights] = await Promise.all([getCampaignApi(id), getInsightsApi(id)]);
      setC(detail);
      setDays(insights.days);
    } catch (e: any) {
      toast.error(e?.message);
      navigate(CAMPANHAS_PAGE(), { replace: true });
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  if (!c) {
    return (
      <div className="cmpPage">
        <main className="cmpPage__main">
          <p className="cmpLoading">Carregando...</p>
        </main>
      </div>
    );
  }

  const draft = c.status === "RASCUNHO" || c.status === "ERRO";
  const live = c.status === "ATIVA" || c.status === "PAUSADA";
  const editable = c.status !== "ENCERRADA" && c.status !== "PUBLICANDO";
  const tab: Tab = tabParam ?? (draft ? "anuncios" : "resultados");
  const setTab = (t: Tab) => setParams({ aba: t }, { replace: true });

  const run = async (action: Action) => {
    try {
      setBusy(true);
      if (action === "publish") {
        await publishCampaignApi(c.id);
        toast.success("Campanha criada na Meta, pausada. Confira e ative quando quiser começar.");
      } else if (action === "activate") {
        await activateCampaignApi(c.id);
        toast.success("Campanha ativada. A Meta revisa os anúncios antes de veicular.");
      } else if (action === "pause") {
        await pauseCampaignApi(c.id);
        toast.success("Campanha pausada.");
      } else if (action === "end") {
        await endCampaignApi(c.id);
        toast.success("Campanha encerrada.");
      } else {
        await deleteCampaignApi(c.id);
        toast.success("Rascunho excluído.");
        navigate(CAMPANHAS_PAGE(), { replace: true });
        return;
      }
      setConfirm(null);
      await load();
    } catch (e: any) {
      toast.error(e?.message);
      setConfirm(null);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    try {
      setBusy(true);
      const r = await syncCampaignApi(c.id);
      toast[r.skipped ? "info" : "success"](r.skipped ? "Os resultados foram atualizados há menos de 5 minutos." : "Resultados atualizados.");
      await load();
    } catch (e: any) {
      toast.error(e?.message);
    } finally {
      setBusy(false);
    }
  };

  const removeAd = async () => {
    if (!adToDelete) return;
    try {
      setBusy(true);
      await deleteAdApi(c.id, adToDelete.id);
      toast.success("Anúncio excluído.");
      setAdToDelete(null);
      await load();
    } catch (e: any) {
      toast.error(e?.message);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!c.applicationUrl) return;
    try {
      await navigator.clipboard.writeText(c.applicationUrl);
      toast.success("Link copiado.");
    } catch {
      toast.info(c.applicationUrl);
    }
  };

  const period = c.endDate ? `${fmtDate(c.startDate)} a ${fmtDate(c.endDate)}` : `a partir de ${fmtDate(c.startDate)}, sem data de fim`;

  const headerActions = (
    <>
      {!draft ? (
        <button type="button" className="ecoBtn" onClick={() => navigate(CAMPANHA_PROXIMA_PAGE(c.id))} disabled={busy}>
          <EcoIcon name="helpCircle" size={18} />
          Próxima com a Eco-IA
        </button>
      ) : null}
      {editable ? (
        <button type="button" className="ecoBtn" onClick={() => navigate(CAMPANHA_EDIT_PAGE(c.id))} disabled={busy}>
          Editar
        </button>
      ) : null}
      {c.status === "RASCUNHO" ? (
        <button type="button" className="ecoBtn ecoBtn--danger" onClick={() => setConfirm("delete")} disabled={busy}>
          Excluir
        </button>
      ) : null}
      {live || c.status === "ERRO" ? (
        <button type="button" className="ecoBtn ecoBtn--danger" onClick={() => setConfirm("end")} disabled={busy}>
          Encerrar
        </button>
      ) : null}
      {live ? (
        <button type="button" className="ecoBtn" onClick={sync} disabled={busy}>
          <EcoIcon name="refresh" size={18} />
          Atualizar agora
        </button>
      ) : null}
      {c.status === "ATIVA" ? (
        <button type="button" className="ecoBtn" onClick={() => setConfirm("pause")} disabled={busy}>
          Pausar
        </button>
      ) : null}
      {c.status === "PAUSADA" ? (
        <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => setConfirm("activate")} disabled={busy}>
          Ativar
        </button>
      ) : null}
      {draft ? (
        <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => setConfirm("publish")} disabled={busy || Boolean(c.publishBlocker)}>
          {c.status === "ERRO" ? "Tentar de novo" : "Publicar na Meta"}
        </button>
      ) : null}
    </>
  );

  const confirmText: Record<Action, { title: string; message: React.ReactNode; label: string; tone?: "danger" }> = {
    publish: {
      title: "Publicar na Meta?",
      message: (
        <>
          A campanha, o conjunto e os {c.ads.length} anúncio(s) são criados na Meta <strong>pausados</strong>: nada é gasto até você ativar.
        </>
      ),
      label: "Publicar pausada",
    },
    activate: {
      title: "Ativar a campanha?",
      message: (
        <>
          A partir de agora a Meta passa a gastar <strong>{budgetLabel(c.budgetType, c.budgetCents)}</strong>, {period}, em{" "}
          {c.locations.map((l) => `${l.label} (${l.radiusKm} km)`).join(", ")}. A cobrança sai da conta de anúncios.
        </>
      ),
      label: "Ativar e começar a gastar",
    },
    pause: { title: "Pausar a campanha?", message: "Os anúncios param de veicular. A página de candidatura continua aberta.", label: "Pausar" },
    end: {
      title: "Encerrar a campanha?",
      message: "A campanha é arquivada na Meta e não volta a veicular. A página de candidatura fecha. Os candidatos continuam guardados.",
      label: "Encerrar",
      tone: "danger",
    },
    delete: { title: "Excluir o rascunho?", message: "O rascunho e os anúncios dele são apagados.", label: "Excluir", tone: "danger" },
  };

  const t = c.totals;

  return (
    <div className="cmpPage">
      <main className="cmpPage__main">
        <EcoPageHeader
          crumbs={[{ label: "Campanhas", onClick: () => navigate(CAMPANHAS_PAGE()) }, { label: c.name }]}
          title={c.name}
          subtitle={
            <>
              <span className={STATUS_PILL[c.status]}>{STATUS_LABEL[c.status]}</span> · {c.jobTitle} · {budgetLabel(c.budgetType, c.budgetCents)} · {period}
            </>
          }
          onBack={goBack}
          backLabel="Voltar para Campanhas"
          actions={headerActions}
        />

        {c.status === "ERRO" && c.lastError ? (
          <p className="cmpNotice cmpNotice--bad">
            <EcoIcon name="alertTriangle" size={16} />
            <span>
              A publicação parou: {c.lastError}. Corrija o que a Meta apontou e clique em "Tentar de novo": o que já foi criado não é criado de novo.
            </span>
          </p>
        ) : null}
        {draft && c.publishBlocker ? (
          <p className="cmpNotice">
            <EcoIcon name="alertTriangle" size={16} />
            <span>Para publicar: {c.publishBlocker}</span>
          </p>
        ) : null}

        {c.applicationUrl && !draft ? (
          <div className="cmpLink">
            <EcoIcon name="link" size={18} />
            <code>{c.applicationUrl}</code>
            <span className="cmpActions">
              <button type="button" className="ecoBtn ecoBtn--sm" onClick={copyLink}>
                <EcoIcon name="copy" size={16} />
                Copiar link
              </button>
              <a className="ecoBtn ecoBtn--sm" href={c.applicationUrl} target="_blank" rel="noreferrer">
                <EcoIcon name="arrowUpRight" size={16} />
                Abrir página
              </a>
            </span>
          </div>
        ) : null}

        {draft ? (
          <SuggestionRefineChat
            suggestion={campaignToSuggestion(c)}
            disabled={busy}
            onChange={async (s) => {
              // Cada ajuste pedido à Eco-IA grava direto neste rascunho.
              try {
                setBusy(true);
                await applySuggestionApi({ ...suggestionToPayload(s, []), campaignId: c.id });
                toast.success("Rascunho atualizado.");
                await load();
              } catch (e: any) {
                toast.error(e?.message);
              } finally {
                setBusy(false);
              }
            }}
          />
        ) : null}

        <section className="ecoCard cmpPanel cmpPanel--flush">
          <div className="cmpPanel__head">
            <EcoSegmented label="Mostrar" options={TABS} value={tab} onChange={setTab} />
            {tab === "resultados" && c.lastSyncedAt ? <span className="cmpHint">Atualizado em {fmtDateTime(c.lastSyncedAt)}</span> : null}
          </div>

          {tab === "resultados" ? (
            <div className="cmpPanel__pad" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <AiAnalysisPanel campaignId={c.id} hasData={Boolean(days?.length) || t.applications > 0} />
              <div className="cmpKpis">
                {[
                  ["Gasto", fmtMoney(t.spendCents)],
                  ["Impressões", fmtInt(t.impressions)],
                  ["Cliques no link", fmtInt(t.linkClicks)],
                  ["Candidaturas", fmtInt(t.applications)],
                  ["Custo por candidato", fmtMoney(t.costPerApplicationCents)],
                ].map(([label, value]) => (
                  <div key={label} className="ecoCard cmpKpi">
                    <span className="cmpKpi__label">{label}</span>
                    <span className="cmpKpi__value">{value}</span>
                  </div>
                ))}
              </div>
              {!days?.length ? (
                <p className="ecoEmpty">
                  {draft ? "Os resultados aparecem depois que a campanha for publicada e ativada." : "Ainda sem resultados. A Meta leva algumas horas para informar o primeiro dia."}
                </p>
              ) : (
                <>
                  <div>
                    <h3 className="cmpPanel__title" style={{ fontSize: 14, marginBottom: 8 }}>
                      Candidaturas por dia
                    </h3>
                    <DailyChart days={days} />
                  </div>
                  <div className="ecoTableWrap">
                    <table className="ecoTable">
                      <thead>
                        <tr>
                          <th scope="col">Dia</th>
                          <th scope="col" className="ecoTable__num">Gasto</th>
                          <th scope="col" className="ecoTable__num">Impressões</th>
                          <th scope="col" className="ecoTable__num">Alcance</th>
                          <th scope="col" className="ecoTable__num">Cliques</th>
                          <th scope="col" className="ecoTable__num">Candidaturas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...days].reverse().map((d) => (
                          <tr key={d.date}>
                            <td>{fmtDate(d.date)}</td>
                            <td className="ecoTable__num">{fmtMoney(d.spendCents)}</td>
                            <td className="ecoTable__num">{fmtInt(d.impressions)}</td>
                            <td className="ecoTable__num">{fmtInt(d.reach)}</td>
                            <td className="ecoTable__num">{fmtInt(d.linkClicks)}</td>
                            <td className="ecoTable__num">{fmtInt(d.applications)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          ) : null}

          {tab === "anuncios" ? (
            <div className="cmpPanel__pad">
              {editable ? <CampaignCreativePanel campaignId={c.id} canAddAd={c.ads.length < 5} onAdAdded={load} /> : null}
              <div className="cmpAds">
                {c.ads.map((ad) => {
                  const s = adStatusLabel(ad.metaEffectiveStatus);
                  return (
                    <article key={ad.id} className="cmpAd">
                      <AdImage campaignId={c.id} adId={ad.id} version={ad.imageAssetId} alt={`Imagem do anúncio ${ad.position}`} className="cmpAd__image" />
                      <div className="cmpAd__body">
                        <span className="cmpCard__top">
                          <span className="cmpStrong">Anúncio {ad.position}</span>
                          <span className={AD_TONE[s.tone]}>{s.label}</span>
                        </span>
                        <p className="cmpAd__text">{ad.primaryText}</p>
                        <span className="cmpAd__headline">{ad.headline}</span>
                        {ad.description ? <span className="cmpSub">{ad.description}</span> : null}
                        {ad.reviewFeedback ? <p className="cmpAd__feedback">Motivo da Meta: {ad.reviewFeedback}</p> : null}
                        <span className="cmpSub">{fmtInt(ad.applications)} candidatura(s) por este anúncio</span>
                      </div>
                      <div className="cmpAd__actions">
                        <button type="button" className="ecoBtn ecoBtn--sm ecoBtn--primary" onClick={() => setAdToPreview(ad)}>
                          <EcoIcon name="eye" size={16} />
                          Pré-visualizar
                        </button>
                      {editable ? (
                        <>
                          <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => setAdModal({ ad })} disabled={busy}>
                            Editar
                          </button>
                          <button
                            type="button"
                            className="ecoBtn ecoBtn--sm ecoBtn--danger"
                            onClick={() => setAdToDelete(ad)}
                            disabled={busy || (live && c.ads.length <= 1)}
                          >
                            Excluir
                          </button>
                        </>
                      ) : null}
                      </div>
                    </article>
                  );
                })}
                {editable && c.ads.length < 5 ? (
                  <button type="button" className="cmpAdEmpty" onClick={() => setAdModal({ ad: null })} disabled={busy}>
                    <EcoIcon name="plus" size={24} />
                    Incluir anúncio
                    <span className="cmpSub">Até 5 por campanha. A Meta mostra mais o que der mais resultado.</span>
                  </button>
                ) : null}
                {editable && c.ads.length < 5 ? (
                  <button type="button" className="cmpAdEmpty" onClick={() => setPicking(true)} disabled={busy}>
                    <EcoIcon name="camera" size={24} />
                    Escolher um criativo da Eco-IA
                    <span className="cmpSub">Uma das artes já geradas vira o anúncio. Depois é só escolher os textos.</span>
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {tab === "candidatos" ? <ApplicationsPanel campaignId={c.id} ads={c.ads} /> : null}

          {tab === "configuracao" ? (
            <div className="cmpPanel__pad">
              <dl className="cmpDl">
                <dt>Título da vaga</dt>
                <dd>{c.jobTitle}</dd>
                <dt>Descrição</dt>
                <dd style={{ whiteSpace: "pre-line" }}>{c.jobDescription}</dd>
                <dt>Perguntas extras</dt>
                <dd>{c.questions.length ? c.questions.map((q) => `${q.label}${q.required ? " (obrigatória)" : ""}`).join(" · ") : "Nenhuma"}</dd>
                <dt>Currículo</dt>
                <dd>{c.resumeRequired ? "Obrigatório" : "Opcional"}</dd>
                <dt>Locais</dt>
                <dd>{c.locations.map((l) => `${l.label} (${l.radiusKm} km)`).join(" · ")}</dd>
                <dt>Orçamento</dt>
                <dd>{budgetLabel(c.budgetType, c.budgetCents)}</dd>
                <dt>Período</dt>
                <dd>{period}</dd>
                <dt>Criada por</dt>
                <dd>
                  {c.createdBy.nomeCompleto}, {fmtDateTime(c.createdAt)}
                </dd>
                {c.publishedAt ? (
                  <>
                    <dt>Publicada em</dt>
                    <dd>{fmtDateTime(c.publishedAt)}</dd>
                  </>
                ) : null}
                {c.endedAt ? (
                  <>
                    <dt>Encerrada em</dt>
                    <dd>{fmtDateTime(c.endedAt)}</dd>
                  </>
                ) : null}
              </dl>
            </div>
          ) : null}
        </section>
      </main>

      {adToPreview ? <AdPreviewModal campaignId={c.id} ad={adToPreview} metaEnabled={c.metaEnabled} onClose={() => setAdToPreview(null)} /> : null}

      {adModal ? (
        <AdFormModal
          campaignId={c.id}
          ad={adModal.ad}
          live={live}
          suggestedAds={c.suggestedAds ?? []}
          onClose={() => setAdModal(null)}
          onSaved={() => {
            setAdModal(null);
            load();
          }}
        />
      ) : null}

      <EcoConfirmDialog
        open={Boolean(confirm)}
        title={confirm ? confirmText[confirm].title : ""}
        message={confirm ? confirmText[confirm].message : null}
        confirmLabel={confirm ? confirmText[confirm].label : ""}
        tone={confirm ? confirmText[confirm].tone : undefined}
        busy={busy}
        onConfirm={() => confirm && run(confirm)}
        onCancel={() => setConfirm(null)}
      />

      {picking ? (
        <CreativePickerModal
          campaignId={c.id}
          onClose={() => setPicking(false)}
          onPick={(creativeId) => {
            setPicking(false);
            setPickedCreative(creativeId);
          }}
        />
      ) : null}

      {pickedCreative ? (
        <UseCreativeAsAdModal
          creativeId={pickedCreative}
          defaultCampaignId={c.id}
          onClose={() => setPickedCreative(null)}
          onDone={() => {
            setPickedCreative(null);
            load();
          }}
        />
      ) : null}

      <AiAssistantDrawer campaignId={c.id} campaignName={c.name} />

      <EcoConfirmDialog
        open={Boolean(adToDelete)}
        title="Excluir o anúncio?"
        message={live ? "O anúncio também é apagado na Meta." : "O anúncio sai do rascunho."}
        confirmLabel="Excluir"
        tone="danger"
        busy={busy}
        onConfirm={removeAd}
        onCancel={() => setAdToDelete(null)}
      />
    </div>
  );
};

export default CampanhaPage;
